import { Component, FunctionComponent } from "react";
import { Auth } from "aws-amplify";
import { RouteComponentProps } from "react-router-dom";
import { Storage } from "../../env";
import {
    IGetPostalDelivery,
    IPostalDeliveryOptIn,
    IPostalDeliveryOptOut,
    IUpdatePostalDelivery,
    PostalDelivery as PostalDeliveryDto,
} from "../../client/core";
import { Alert, Col, Form, Row, Card } from "react-bootstrap";
import BootstrapSwitchButton from "bootstrap-switch-button-react";
import { Button, variant } from "../form/Button";

export enum PostalDeliveryPriceAction {
    View,
    Update,
}

export interface PostalDeliveriesState {
    postalDelivery?: PostalDeliveryDto;
    error: string;
    priceAction: PostalDeliveryPriceAction;
    pendingPrice: number;
}

export interface PostalDeliveriesProps extends RouteComponentProps {
    auth: typeof Auth;
    getPostalDeliveryAPI: IGetPostalDelivery;
    postalDeliveryOptInAPI: IPostalDeliveryOptIn;
    postalDeliveryOptOutAPI: IPostalDeliveryOptOut;
    updatePostalDeliveryAPI: IUpdatePostalDelivery;
}

export default class PostalDeliveries extends Component<PostalDeliveriesProps, PostalDeliveriesState> {
    constructor(props: PostalDeliveriesProps) {
        super(props);
        this.state = {
            error: "",
            priceAction: PostalDeliveryPriceAction.View,
            pendingPrice: 0,
        };
    }

    componentDidMount() {
        this.props.auth
            .currentSession()
            .then((session) => {
                this.props.getPostalDeliveryAPI
                    .GetPostalDelivery({
                        identityToken: session.getIdToken().getJwtToken(),
                        shopID: localStorage.getItem(Storage.ShopID)!,
                    })
                    .then((response) => {
                        if (response.statusCode === 403) {
                            localStorage.removeItem(Storage.ShopID);
                            return this.setState({
                                error: "You are forbidden to access this shop's postal delivery settings. Please navigate back to the shop dashboard.",
                            });
                        }

                        if (response.statusCode !== 200) {
                            return this.setState({
                                error: "Unable to retrieve the current postal delivery settings",
                            });
                        }

                        this.setState({ postalDelivery: response.postalDelivery });
                    })
                    .catch(() => {
                        this.setState({ error: "Unable to retrieve the current postal delivery settings" });
                    });
            })
            .catch(() => {
                this.setState({ error: "Unable to retrieve the current postal delivery settings" });
            });
    }

    handleToggle = (enabled: boolean): void => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (!shopID) return;

        this.props.auth.currentSession().then((session) => {
            const token = session.getIdToken().getJwtToken();

            if (enabled) {
                this.props.postalDeliveryOptInAPI
                    .PostalDeliveryOptIn({ identityToken: token, shopID })
                    .then((response) => {
                        if (response.statusCode === 403) {
                            localStorage.removeItem(Storage.ShopID);
                            return this.setState({ error: "You are forbidden to update postal delivery settings. Please navigate back to the shop home dashboard to resync." });
                        }
                        if (response.statusCode !== 200) {
                            return this.setState({ error: "Failed to enable postal delivery" });
                        }
                        this.setState({ postalDelivery: { ...this.state.postalDelivery, isEnabled: true } });
                    });
            } else {
                this.props.postalDeliveryOptOutAPI
                    .PostalDeliveryOptOut({ identityToken: token, shopID })
                    .then((response) => {
                        if (response.statusCode === 403) {
                            localStorage.removeItem(Storage.ShopID);
                            return this.setState({ error: "You are forbidden to update postal delivery settings. Please navigate back to the shop home dashboard to resync." });
                        }
                        if (response.statusCode !== 200) {
                            return this.setState({ error: "Failed to disable postal delivery" });
                        }
                        this.setState({ postalDelivery: { ...this.state.postalDelivery!, isEnabled: false } });
                    });
            }
        });
    };

    handleUpdatePrice = (price: number): void => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (!shopID) return;

        this.props.auth.currentSession().then((session) => {
            const token = session.getIdToken().getJwtToken();
            this.props.updatePostalDeliveryAPI
                .UpdatePostalDelivery({ identityToken: token, shopID, price })
                .then((response) => {
                    if (response.statusCode === 403) {
                        localStorage.removeItem(Storage.ShopID);
                        return this.setState({
                            error: "You are forbidden to update postal delivery settings. Please navigate back to the shop home dashboard to resync.",
                        });
                    }
                    if (response.statusCode !== 200) {
                        return this.setState({ error: "Unable to update postal delivery price" });
                    }
                    this.setState({
                        postalDelivery: { ...this.state.postalDelivery!, price },
                        priceAction: PostalDeliveryPriceAction.View,
                    });
                });
        });
    };

    render() {
        return (
            <PostalDeliveriesView
                postalDelivery={this.state.postalDelivery}
                error={this.state.error}
                onToggle={this.handleToggle}
                priceAction={this.state.priceAction}
                onChangePriceAction={(action) => this.setState({ priceAction: action })}
                onUpdatePrice={this.handleUpdatePrice}
            />
        );
    }
}

export interface PostalDeliveriesViewProps {
    postalDelivery?: PostalDeliveryDto;
    error: string;
    onToggle: (enabled: boolean) => void;
    priceAction: PostalDeliveryPriceAction;
    onChangePriceAction: (action: PostalDeliveryPriceAction) => void;
    onUpdatePrice: (price: number) => void;
}

export const PostalDeliveriesView: FunctionComponent<PostalDeliveriesViewProps> = (props) => (
    <div>
        <Row>
            <Col>
                <h1 style={{ marginBottom: "2rem" }}>Postal Deliveries</h1>
            </Col>
            <Col>
                <div style={{ float: "right" }}>
                    <BootstrapSwitchButton
                        checked={props.postalDelivery?.isEnabled ?? false}
                        onstyle={"success"}
                        offstyle={"secondary"}
                        width={150}
                        onlabel="Enabled"
                        offlabel="Disabled"
                        onChange={props.onToggle}
                    />
                </div>
            </Col>
        </Row>
        <Alert variant={"danger"} show={props.error !== ""}>
            {props.error}
        </Alert>
        {props.postalDelivery && (
            <PostalDeliveryPrice
                price={props.postalDelivery.price}
                action={props.priceAction}
                onChangeAction={props.onChangePriceAction}
                onSubmit={props.onUpdatePrice}
            />
        )}
    </div>
);

export interface PostalDeliveryPriceProps {
    price?: number;
    action: PostalDeliveryPriceAction;
    onChangeAction: (action: PostalDeliveryPriceAction) => void;
    onSubmit: (price: number) => void;
}

export const PostalDeliveryPrice: FunctionComponent<PostalDeliveryPriceProps> = (props) => {
    if (props.action === PostalDeliveryPriceAction.Update) {
        return <PostalDeliveryPriceUpdateForm {...props} />;
    }
    return <PostalDeliveryPriceView {...props} />;
};

const PostalDeliveryPriceView: FunctionComponent<PostalDeliveryPriceProps> = (props) => (
    <Card className="elevate" style={{ marginTop: "1rem" }}>
        <Card.Body>
            <Row>
                <Col xs={12} sm={10}>
                    <Card.Title style={{ paddingBottom: ".3rem" }}>Postal Delivery Price</Card.Title>
                    <Card.Subtitle style={{ paddingBottom: ".5rem" }}>
                        Set a postal delivery price in <strong>pounds</strong>
                    </Card.Subtitle>
                    <Card.Text>
                        {props.price
                            ? `Current postal delivery price: £${props.price.toFixed(2)}`
                            : "No postal delivery price set"}
                    </Card.Text>
                </Col>
                <Col xs={12} sm={2}>
                    {props.price ? (
                        <Button
                            id={"postal-delivery-price-edit-btn"}
                            name={"Edit"}
                            buttonvariant={variant.Primary}
                            style={{ float: "right" }}
                            onClick={() => props.onChangeAction(PostalDeliveryPriceAction.Update)}
                        />
                    ) : (
                        <Button
                            id={"postal-delivery-price-set-btn"}
                            name={"Set Price"}
                            buttonvariant={variant.Primary}
                            style={{ float: "right" }}
                            onClick={() => props.onChangeAction(PostalDeliveryPriceAction.Update)}
                        />
                    )}
                </Col>
            </Row>
        </Card.Body>
    </Card>
);

const PostalDeliveryPriceUpdateForm: FunctionComponent<PostalDeliveryPriceProps> = (props) => {
    let enteredPrice = 0;

    return (
        <Card className="elevate" style={{ marginTop: "1rem" }}>
            <Card.Body>
                <Card.Title style={{ paddingBottom: ".3rem" }}>Postal Delivery Price</Card.Title>
                <Card.Subtitle style={{ paddingBottom: ".5rem" }}>
                    Set a postal delivery price in <strong>pounds</strong>
                </Card.Subtitle>
                <Form>
                    <Row>
                        <Col xs={9}>
                            <Form.Group>
                                <Form.Label htmlFor="postal-price">price</Form.Label>
                                <Form.Control
                                    id={"postal-price"}
                                    type={"number"}
                                    step={".01"}
                                    name={"postal-price"}
                                    placeholder="price"
                                    onChange={(e) => { enteredPrice = Number(e.target.value); }}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={3}>
                            <span>in pounds</span>
                        </Col>
                    </Row>
                    <Row style={{ marginTop: "1rem" }}>
                        <Col>
                            <Button
                                buttonvariant={variant.Secondary}
                                name={"Cancel"}
                                style={{ margin: "0.1rem" }}
                                onClick={() => props.onChangeAction(PostalDeliveryPriceAction.View)}
                            />
                            <Button
                                id={"postal-delivery-price-submit-btn"}
                                name={"Submit"}
                                buttonvariant={variant.Primary}
                                onClick={() => props.onSubmit(enteredPrice)}
                            />
                        </Col>
                    </Row>
                </Form>
            </Card.Body>
        </Card>
    );
};
